import axios, { AxiosError, HttpStatusCode } from "axios";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { PaginationQuery } from "types/feedback-request";

type HttpErrorType = AxiosError<IHttpError> | undefined;

interface IHttpQueryRequestOption {
  enabled: boolean;
}

interface IHttpRequestParam<TResponse> {
  request: () => Promise<TResponse>;
  requestOption?: IHttpQueryRequestOption;
  deps?: readonly unknown[];
  onLoad?: () => void;
  onCompleted?: () => void;
}

interface IHttpQueryResponse<TResponse> {
  result: TResponse | undefined;
  refetch: () => Promise<void>;
  error: HttpErrorType;
  isLoading: boolean;
}

export const useHttpQueryService = <TResponse>({
  request,
  requestOption = {
    enabled: true,
  },
  deps = [],
}: IHttpRequestParam<TResponse>): IHttpQueryResponse<TResponse> => {
  const requestRef = useRef(request);
  requestRef.current = request;

  const [result, setResult] = useState<TResponse>();
  const [error, setError] = useState<HttpErrorType>();
  const [isLoading, setIsLoading] = useState<boolean>(
    requestOption.enabled === true,
  );

  const fetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await requestRef.current();
      setResult(res);
      setError(undefined);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err);
      }

      console.log("Request error: ", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (!requestOption.enabled) {
      return;
    }

    setIsLoading(true);
    setResult(undefined);
    setError(undefined);
  }, [requestOption.enabled, ...deps]);

  useEffect(() => {
    if (!requestOption.enabled) {
      return;
    }

    fetch();
  }, [requestOption.enabled, fetch, ...deps]);

  const refetch = useCallback(async () => {
    await fetch();
  }, [fetch]);

  return { result, error, isLoading, refetch };
};

interface IHttpPaginatedQueryParam<TItem> {
  fetchPage: (query: PaginationQuery) => Promise<TItem[]>;
  take: number;
  requestOption?: IHttpQueryRequestOption;
  deps?: readonly unknown[];
}

interface IHttpPaginatedQueryResponse<TItem> {
  items: TItem[];
  hasMore: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: HttpErrorType;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useHttpPaginatedQueryService = <TItem>({
  fetchPage,
  take,
  requestOption = { enabled: true },
  deps = [],
}: IHttpPaginatedQueryParam<TItem>): IHttpPaginatedQueryResponse<TItem> => {
  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;

  const [items, setItems] = useState<TItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<HttpErrorType>();
  const [isLoading, setIsLoading] = useState(requestOption.enabled);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  const fetchInitial = useCallback(async () => {
    setIsLoading(true);
    try {
      const page = await fetchPageRef.current({ take, skip: 0 });
      setItems(page);
      setHasMore(page.length === take);
      setError(undefined);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err);
      }
      console.log("Request error: ", err);
    } finally {
      setIsLoading(false);
    }
  }, [take]);

  useEffect(() => {
    setItems([]);
    setHasMore(false);

    if (!requestOption.enabled) {
      return;
    }

    fetchInitial();
  }, [requestOption.enabled, fetchInitial, ...deps]);

  const loadMore = useCallback(async () => {
    if (isFetchingNextPage || !hasMore) {
      return;
    }

    setIsFetchingNextPage(true);
    try {
      const page = await fetchPageRef.current({
        take,
        skip: items.length,
      });
      setItems((prev) => [...prev, ...page]);
      setHasMore(page.length === take);
      setError(undefined);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err);
      }
      console.log("Request error: ", err);
    } finally {
      setIsFetchingNextPage(false);
    }
  }, [isFetchingNextPage, hasMore, items.length, take]);

  return {
    items,
    hasMore,
    isLoading,
    isFetchingNextPage,
    error,
    refetch: fetchInitial,
    loadMore,
  };
};

interface IHttpMutationParams<Tpayload, TResponse> {
  request: (payload?: Tpayload) => Promise<TResponse>;
  onLoad?: () => void;
  onCompleted?: () => void;
}

interface IHttpMutationResponse<Tpayload, TResponse> {
  mutate: (params?: Tpayload) => Promise<TResponse | undefined>;
  result: TResponse | undefined;
  error: HttpErrorType;
  isLoading: boolean;
}

interface IHttpError {
  error: string;
  message: string;
  statusCode: HttpStatusCode;
}

export const useHttpMutationService = <Tpayload, TResponse>({
  request,
}: IHttpMutationParams<Tpayload, TResponse>): IHttpMutationResponse<
  Tpayload,
  TResponse
> => {
  const [result, setResult] = useState<TResponse>();
  const [error, setError] = useState<HttpErrorType>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const mutate = useCallback(
    async (payload?: Tpayload) => {
      setIsLoading(true);
      try {
        const result = await request(payload);
        setResult(result);
        setIsLoading(false);
        return result;
      } catch (err) {
        if (axios.isAxiosError(err)) {
          setError(err);
        }

        setIsLoading(false);
      }
    },
    [request],
  );

  return { mutate, result, error, isLoading };
};
